import { isNullableType } from "graphql";
import { AssetLink, SoilProfile } from "../../../types/types"
import { dcRound } from "../../../utils/misc"
import { range } from "lodash";

const toFiniteNumbers = (values: any[]): number[] =>
  values
    .map((v: any) => Number(v))
    .filter((v: number) => Number.isFinite(v));

const hasFiniteValues = (values: any[]): boolean => toFiniteNumbers(values).length > 0;

const hasFiniteValueInRange = (values: any[], min: number, max: number): boolean =>
  toFiniteNumbers(values).some((v: number) => v >= min && v <= max);

const median = (values: any[]): number | null => {
  const nums = toFiniteNumbers(values).sort((a: number, b: number) => a - b);
  if (!nums.length) return null;
  const mid = Math.floor(nums.length / 2);
  return nums.length % 2 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
};

const getFirstFiniteSeries = (df: any, columns: string[]) => {
  const availableColumns = df.listColumns();
  for (const column of columns) {
    if (!availableColumns.includes(column)) continue;

    const values = df.toArray(column);
    if (hasFiniteValues(values)) {
      return { column, values };
    }
  }

  return { column: null, values: [] as any[] };
};

// NEW: build inch labels while keeping depth data in cm
const makeInchTicksFromCm = (df: any) => {
  const availableColumns = df.listColumns();
  const hasDepth = availableColumns.includes('depth (cm)');
  const maxDepth = hasDepth ? df.stat.max('depth (cm)') : 60;

  // Show ticks every 10 cm (your existing dtick), labeled as 2.5 in, 5.0 in, 7.5 in, ...
  const cmStep = 10;
  const cmToInDisplay = (1 / 2.5); // 10 cm = 2.5 in

  const tickvals: number[] = [];
  const ticktext: string[] = [];
  for (let cm = 0; cm <= maxDepth; cm += cmStep) {
    tickvals.push(cm);                         // actual positions (cm)
    ticktext.push((cm * cmToInDisplay).toFixed(1)); // labels (inches)
  }

  return { hasDepth, maxDepth, tickvals, ticktext };
};


const dTickCalibrate = (useInches: boolean, df: any) => {
  let availableColumns = df.listColumns();
  const maxDepth =  availableColumns.includes('depth (cm)') ? df.stat.max('depth (cm)') : 0;
  let { interval, modulus } = getIntervalModulus(maxDepth);
  console.log("B101625 maxDepth", maxDepth);
  console.log("B101625 modulus", modulus);
  console.log("B101625 useInches", useInches);
  return useInches ? (interval / 2.5) : interval; // inches or cm

}

const getIntervalModulus = (maxDepth: number) => {
  let interval = 10; // interval at which to show images
  let modulus = 5; // starting point of the interval
  // at 10 and 5, this means images at 5, 15, 25, 35, 45, 55, etc.
  // at 5 and 2, this means images at 2, 7, 12, 17, 22, 27, 32, 37, 42, 47, 52, 57, etc.
  if(maxDepth <= 40) { // At < 40 cm, the image aspect ratio begins to be too wide and without stretching it vertically, it will begin to show gaps between images
      interval = 5;
      modulus = 2;
  }/*else if(maxDepth <= 60) {
      interval = 6;
      modulus = 3;
  }*/
  return { interval, modulus };
}

const loadImages = (soilProfile: SoilProfile, df: any, preferredLenSmallAbbr: string) => {
    let availableColumns = df.listColumns();
    const maxDepth =  availableColumns.includes('depth (cm)') ? df.stat.max('depth (cm)') : 0;
    console.log("B101625 maxDepth", maxDepth);
    let { interval, modulus } = getIntervalModulus(maxDepth);
    let images = soilProfile.assetLinks
        .filter((link: AssetLink) => link.asset_type == 'depth_thumbnail' && link.value % interval === modulus)
        .map((link: AssetLink, index: number) => {
            return {
                name: link.value + 'cm',
                source: link.s3_key,  
                xref: "x domain",
                x:0,
                //y: (index * interval) - (interval/2),
                y: (index * interval), //+ 5, // ORIGINAL
                yref: "y",
                opacity: 1,
                layer: "below",
                xanchor: "left",
                yanchor:"top",
                sizex:1,
                sizey:55
            };
        });
    if(!images.length) {
        images = soilProfile.assetLinks
            .filter((link: AssetLink) => link.asset_type == 'depth_snapshot' && link.value % interval === modulus)
            .map((link: AssetLink, index: number) => {
                return {
                    name: link.value + 'cm',
                    source: link.s3_key,
                    xref: "x domain",
                    x:0,
                    y: (index * interval),// + 5,
                    yref: "y",
                    opacity: 1,
                    layer: "below",
                    xanchor: "left",
                    yanchor:"top",
                    sizex:1,
                    sizey:55
                };
            });
    }
    return images;
}

export const texturePlot = (soilProfile: SoilProfile, df: any, preferredLenSmallAbbr: string) => {
    const images = loadImages(soilProfile, df, preferredLenSmallAbbr);
    let availableColumns = df.listColumns();

    // NEW: inch tick labels (display only)
    const { hasDepth, maxDepth, tickvals, ticktext } = makeInchTicksFromCm(df);

    // NEW: convert depth to inches for plotting/hover (dataframe remains in cm)
    const depthCmArr = hasDepth ? df.toArray('depth (cm)') : [];
    const useInches = preferredLenSmallAbbr !== 'cm';
    const cmToIn = (1 / 2.5);
    const depthInArr = hasDepth ? depthCmArr.map((v: any) => (Number(v) * cmToIn).toFixed(1)) : [];
    const maxDepthIn = (hasDepth ? maxDepth : 60) * cmToIn;

    // NEW: convert imagery positions/sizes to inches so they stay aligned
    const imagesInches = images.map((img: any) => ({
        ...img,
        y: (typeof img.y === 'number' ? img.y * cmToIn : img.y),
        sizey: (typeof img.sizey === 'number' ? img.sizey * cmToIn : img.sizey),
    }));

    return {
        title: "Texture",
        traces: [{
            x: availableColumns.includes('sand') ? df.toArray('sand') : [],
            y: useInches ? depthInArr : depthCmArr, // inches or cm
            name: 'Sand',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line: { color: 'yellow' },
            xaxis: 'x2',
            yaxis: 'y',
            stackgroup: 'g1',
            orientation: 'h',
        },
        {
            x: availableColumns.includes('silt') ? df.toArray('silt') : [],
            y: useInches ? depthInArr : depthCmArr, // inches or cm
            name: 'Silt',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line: { color: 'orange' },
            xaxis: 'x2',
            yaxis: 'y',
            stackgroup: 'g1',
            orientation: 'h',
        },
        {
            x: availableColumns.includes('clay') ? df.toArray('clay') : [],
            y: useInches ? depthInArr : depthCmArr, // inches or cm
            name: 'Clay',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line: { color: 'red' },
            xaxis: 'x2',
            yaxis: 'y',
            stackgroup: 'g1',
            orientation: 'h',
        },
        {
            x: availableColumns.includes('mean_val') ? df.toArray('mean_val') : [],
            y: useInches ? depthInArr : depthCmArr, // inches or cm
            name: 'Value',
            type: 'scatter',
            connectgaps: true,
            showlegend: false,
            visible: false,
            xaxis: 'x',
            yaxis: 'y',
            orientation: 'h'
        }],
        layout: {
            hovermode:"y",
            margin: {
                t: 0,
                b: 0,
                l: 40,
                r: 0
            },
            showLegend: true,
            legend: {
                orientation: "h",
                x: 0, y: 0,
                traceorder: "normal"
            },
            grid: {
                subplots: [["xy", "x2y"]]
            },
            xaxis: {
                side: "top",
                type: "linear",
                title: { text: "Imagery" },
                range: [0, 100],
                domain: [0, 0.4],
                autorange: true,
                showgrid: false,
                showticklabels: false
            },
            xaxis2: {
                side: "top",
                type: "linear",
                range: [0, 1],
                title: { "text": "Texture" },
                domain: [0.5, 0.9],
                autorange: true
            },
            yaxis: {
                side: "left",
                type: "linear",
                // DISPLAY inches only; data remains cm
                title: { text: useInches ? "depth (in)" : "depth (cm)", standoff: 0 },
                range: [useInches ? maxDepthIn : (hasDepth ? maxDepth : 60), 0], // include all data + images; depth increases downward
                domain: [0, 0.9],
                autorange: false,
                // dtick: 10.0, // keep for reference; ticks are now explicitly set
                // NOTE: axis ticks/grid now naturally match chosen unit
                dtick: dTickCalibrate(useInches, df),
                //dtick: useInches ? 4.0 : 10.0,
            },
            autosize: true,
            images: useInches ? imagesInches : images, // unchanged visuals; y’s match unit
        }
    }
}


export const colorPlot = (soilProfile: SoilProfile, df: any, preferredLenSmallAbbr: string) => {
    const images = loadImages(soilProfile, df, preferredLenSmallAbbr);
    let availableColumns = df.listColumns();

    // NEW: convert depth to inches for plotting/hover (dataframe remains in cm)
    const hasDepth = availableColumns.includes('depth (cm)');
    const depthCmArr = hasDepth ? df.toArray('depth (cm)') : [];
    const useInches = preferredLenSmallAbbr !== 'cm';
    const cmToIn = (1 / 2.5); // (0.4)
    const depthInArr = hasDepth ? depthCmArr.map((v: any) => (Number(v) * cmToIn).toFixed(1)) : [];
    const maxDepthCm = hasDepth ? df.stat.max('depth (cm)') : 60;
    const maxDepthIn = maxDepthCm * cmToIn;

    // NEW: convert imagery positions/sizes to inches so they stay aligned
    const imagesInches = images.map((img: any) => ({
        ...img,
        y: (typeof img.y === 'number' ? img.y * cmToIn : img.y),
        sizey: (typeof img.sizey === 'number' ? img.sizey * cmToIn : img.sizey),
    }));
    console.log("091125 images", images);
    console.log("091125 imagesInches", imagesInches);
    console.log("091125 depthInArr", depthInArr);
    console.log("091125 depthCmArr", depthCmArr);
    return {
        title: "Color",
        traces: [{
            x: availableColumns.includes('mean_hue') ? df.toArray('mean_hue') : [],
            y: useInches ? depthInArr : depthCmArr, // inches or cm
            name: 'Hue',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'none',
            line: { color: 'orange' },
            xaxis: 'x2',
            yaxis: 'y',
            showTitle: false
        },
        {
            x: availableColumns.includes('mean_val') ? df.toArray('mean_val') : [],
            y: useInches ? depthInArr : depthCmArr, // inches or cm
            name: 'Value',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'none',
            line: { color: 'darkgray' },
            xaxis: 'x2',
            yaxis: 'y',
            orientation: 'h',
        },
        {
            x: availableColumns.includes('mean_sat') ? df.toArray('mean_sat') : [],
            y: useInches ? depthInArr : depthCmArr, // inches or cm
            name: 'Saturation',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'none',
            line: { color: '#28a745"' },
            xaxis: 'x2',
            yaxis: 'y',
            orientation: 'h'
        },
        {
            x: availableColumns.includes('mean_val') ? df.toArray('mean_val') : [],
            y: useInches ? depthInArr : depthCmArr, // inches or cm
            name: 'Value',
            type: 'scatter',
            connectgaps: true,
            showlegend: false,
            visible: false,
            xaxis: 'x',
            yaxis: 'y',
            orientation: 'h'
        }],
        layout: {
            hovermode:"y", // keep original appearance so the black badge shows
            margin: {
                t: 0,
                b: 0,
                l: 40,
                r: 0
            },
            showLegend: true,
            legend: {
                orientation: "h",
                x: 0, y: 0,
                traceorder: "normal"
            },
            grid: {
                subplots: [["xy", "x2y"]]
            },
            xaxis: {
                side: "top",
                type: "linear",
                title: { text: "Imagery" },
                range: [0, 100],
                domain: [0, 0.4],
                autorange: true,
                showgrid: false,
                showticklabels: false
            },
            xaxis2: {
                side: "top",
                type: "linear",
                range: [0, 1],
                title: { "text": "Color" },
                domain: [0.5, 0.9],
                autorange: true,
            },
            yaxis: {
                side: "left",
                type: "linear",
                title: { text: useInches ? "depth (in)" : "depth (cm)", standoff: 0 }, // display chosen unit
                range: [useInches ? maxDepthIn : maxDepthCm, 0], // keep depth increasing downward
                domain: [0, 0.9],
                autorange: false,
                dtick: dTickCalibrate(useInches, df),
                //dtick: useInches ? 4.0 : 10.0,
            },
            autosize: true,
            images: useInches ? imagesInches : images, // converted to inches when needed
        }
    }
}

export const structurePlot = (df: any, preferredLenSmallAbbr: string) => {
    let availableColumns = df.listColumns();
    // If all pores values are between 0 and 1, we assume they are in percentage and multiply by 100
    let poresArray = []; 
    let modifiedPoresArray = []; 
    if (availableColumns.includes('pores')){
        poresArray = df.toArray('pores');
        const allValuesBetween0And1 = poresArray.every((value: number) => value >= 0 && value <= 1);
        modifiedPoresArray = allValuesBetween0And1 ? poresArray : poresArray.map((value: number) => value / 100);
    }
    // same as above but with 
    let succolarityArray = []; 
    let modifiedSuccolarityArray = []; 
    if (availableColumns.includes('succolarity')){
        succolarityArray = df.toArray('succolarity');
        const succolarityAllValuesBetween0And1 = succolarityArray.every((value: number) => value >= 0 && value <= 1);

        modifiedSuccolarityArray = succolarityAllValuesBetween0And1 ? succolarityArray : succolarityArray.map((value: number) => value / 100) ;
    }
    //console.log("103024 modifiedPoresArray", modifiedPoresArray);
    //console.log("103024 poresArray", poresArray);
    //console.log("103024 modifiedSuccolarityArray", modifiedSuccolarityArray);
    //console.log("103024 succolarityArray", succolarityArray);

    // NEW: inch tick labels (display only)
    const { hasDepth, maxDepth, tickvals, ticktext } = makeInchTicksFromCm(df);

    // NEW: convert depth to inches for plotting/hover (dataframe remains in cm)
    const depthCmArr = hasDepth ? df.toArray('depth (cm)') : [];
    const useInches = preferredLenSmallAbbr !== 'cm';
    const cmToIn = (1 / 2.5);
    const depthInArr = hasDepth ? depthCmArr.map((v: any) => (Number(v) * cmToIn).toFixed(1)) : [];
    const maxDepthIn = (hasDepth ? maxDepth : 60) * cmToIn;

    return {
        title: "Structure",
        traces: [{
            x: availableColumns.includes('qt') ? df.toArray('qt') : [],
            y: useInches ? depthInArr : depthCmArr, // inches or cm
            name: 'PR = Penetration Resistance',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line: { color: 'orange' },
            xaxis: 'x',
            yaxis: 'y',
            orientation: 'h'
        },
        {
            x: modifiedPoresArray,
            y: useInches ? depthInArr : depthCmArr, // inches or cm
            name: 'MP = Macroporosity',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line: { color: 'green' },
            xaxis: 'x2',
            yaxis: 'y',
            orientation: 'h'
        },
        {
            x: modifiedSuccolarityArray,
            y: useInches ? depthInArr : depthCmArr, // inches or cm
            name: 'PC = Pore Connectivity',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line: { color: 'blue' },
            xaxis: 'x3',
            yaxis: 'y',
            orientation: 'h',
        }],
        layout: {
            hovermode:"y",
            margin: {
                t: 0,
                b: 0,
                l: 40,
                r: 0
            },
            showLegend: true,
            legend: {
                orientation: "h",
                x: 0, y: 0,
                traceorder: "normal"
            },
            xaxis: {
                side: "top",
                type: "linear",
                title: { text: "PR", standoff: 0 },
                range: [0, 1500],
                domain: [0, 0.25],
                autorange: true,
            },
            xaxis2: {
                side: "top",
                type: "linear",
                title: { text: "MP", standoff: 0 },
                range: [0, 100],
                domain: [0.3, 0.55],
                autorange: true,
                tickformat: ',.0%',
            },
            xaxis3: {
                side: "top",
                type: "linear",
                title: { text: "PC", standoff: 0 },
                range: [0, 100],
                domain: [0.6, 0.85],
                autorange: true,
                tickformat: ',.0%',
            },
            yaxis: {
                side: "left",
                type: "linear",
                title: { text: useInches ? "depth (in)" : "depth (cm)" },
                range: [useInches ? maxDepthIn : (hasDepth ? maxDepth : 60), 0],
                domain: [0, 0.85],
                autorange: false,
                //dtick: 10.0
                dtick: dTickCalibrate(useInches, df),
                //dtick: useInches ? 4.0 : 10.0,
            },
            autosize: true,
        }
    }
}

export const waterPlot = (soilProfile: SoilProfile, df: any, preferredLenSmallAbbr: string) => {
    let availableColumns = df.listColumns();
    const images = loadImages(soilProfile, df, preferredLenSmallAbbr);

    //(ORIGINAL, will use again once calibrations are improved)df = availableColumns.includes('moisture_cal') ? df.map((row:any) => row.set('moisture_cal', row.get('moisture_cal') / 100)) : null;
    df = df.withColumn('moisture_cal', (row:any) => Math.min(
            (availableColumns.includes('poros') 
                ? row.get('poros') 
                : 0
            ), 
            ((availableColumns.includes('moisture_cal') 
                ? (row.get('moisture_cal') < 0) 
                    ? 0 
                    : row.get('moisture_cal') 
                : 0) 
            / 100)
        ));
    // If all pores values are between 0 and 1, we assume they are in percentage and multiply by 100
    let wiltingPointArray = []; 
    let modifiedWiltingPointArray = []; 
    if ( availableColumns.includes('pwp')){
        wiltingPointArray = df.toArray('pwp');
        const allValuesBetween0And1 = wiltingPointArray.every((value: number) => value >= 0 && value <= 1);
        modifiedWiltingPointArray = allValuesBetween0And1 ? wiltingPointArray : wiltingPointArray.map((value: number) => value / 100);
    }
    let fcPointArray = []; 
    let modifiedfcPointArray = []; 
    if ( availableColumns.includes('fc')){
        fcPointArray = df.toArray('fc');
        const allValuesBetween0And1FC = fcPointArray.every((value: number) => value >= 0 && value <= 1);
        modifiedfcPointArray = allValuesBetween0And1FC ? fcPointArray : fcPointArray.map((value: number) => value / 100);
    }
    let mPointArray = []; 
    let modifiedmPointArray = []; 
    if (availableColumns.includes('moisture_cal')){
        mPointArray = df.toArray('moisture_cal');
        const allValuesBetween0And1M = mPointArray.every((value: number) => value >= 0 && value <= 1);
        modifiedmPointArray = allValuesBetween0And1M ? mPointArray : mPointArray.map((value: number) => value / 100);
    }

    //console.log("103024 modifiedWiltingPointArray", modifiedWiltingPointArray);
    //console.log("103024 modifiedfcPointArray", modifiedfcPointArray);
    //console.log("103024 modifiedmPointArray", modifiedmPointArray);

    // NEW: inch tick labels (display only)
    const { hasDepth, maxDepth, tickvals, ticktext } = makeInchTicksFromCm(df);

    // NEW: convert depth to inches for plotting/hover (dataframe remains in cm)
    const depthCmArr = hasDepth ? df.toArray('depth (cm)') : [];
    const useInches = preferredLenSmallAbbr !== 'cm';
    const cmToIn = (1 / 2.5);
    const depthInArr = hasDepth ? depthCmArr.map((v: any) => (Number(v) * cmToIn).toFixed(1)) : [];
    const maxDepthIn = (hasDepth ? maxDepth : 60) * cmToIn;

    // NEW: convert imagery positions/sizes to inches so they stay aligned
    const imagesInches = images.map((img: any) => ({
        ...img,
        y: (typeof img.y === 'number' ? img.y * cmToIn : img.y),
        sizey: (typeof img.sizey === 'number' ? img.sizey * cmToIn : img.sizey),
    }));
    return {
        title: "Water",
        traces: [{
            x: modifiedWiltingPointArray,
            y: useInches ? depthInArr : depthCmArr, // inches or cm
            name: 'Wilting Point',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line: { color: 'orange' },
            xaxis: 'x2',
            yaxis: 'y',
            orientation: 'h'
        },
        {
            x: modifiedfcPointArray,
            y: useInches ? depthInArr : depthCmArr, // inches or cm
            name: 'Field Capacity',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line: { color: 'green' },
            xaxis: 'x2',
            yaxis: 'y',
            orientation: 'h',
        },
        {
            x: availableColumns.includes('poros') ? df.toArray('poros') : [],
            y: useInches ? depthInArr : depthCmArr, // inches or cm
            name: 'Saturation',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line: { color: 'blue' },
            xaxis: 'x2',
            yaxis: 'y',
            orientation: 'h',
        },
        {
            x:  modifiedmPointArray,
            y: useInches ? depthInArr : depthCmArr, // inches or cm
            name: 'Moisture',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'none',
            line: { color: 'darkblue' },
            xaxis: 'x2',
            yaxis: 'y',
            orientation: 'h',
        },
        {
            x: availableColumns.includes('mean_val') ? df.toArray('mean_val') : [],
            y: useInches ? depthInArr : depthCmArr, // inches or cm
            name: 'Value',
            type: 'scatter',
            connectgaps: true,
            showlegend: false,
            visible: false,
            xaxis: 'x',
            yaxis: 'y',
            orientation: 'h'
        }],
        layout: {
            hovermode:"y",
            margin: {
                t: 0,
                b: 0,
                l: 40,
                r: 0
            },
            showLegend: true,
            legend: {
                orientation: "h",
                x: 0, y: 0,
                traceorder: "normal"
            },
            grid: {
                subplots: [["xy", "x2y"]]
            },
            xaxis: {
                side: "top",
                type: "linear",
                title: { text: "Imagery" },
                range: [0, 100],
                domain: [0, 0.4],
                autorange: true,
                showgrid: false,
                showticklabels: false
            },
            xaxis2: {
                side: "top",
                type: "linear",
                title: { text: "Soil Water", standoff: 0 },
                range: [0, 100],
                domain: [0.5, 0.9],
                autorange: true,
                tickformat: ',.0%',
            },
            yaxis: {
                side: "left",
                type: "linear",
                title: { text: useInches ? "depth (in)" : "depth (cm)", standoff: 0 },
                range: [useInches ? maxDepthIn : (hasDepth ? maxDepth : 60), 0],
                domain: [0, 0.9],
                autorange: false,
                //dtick: 10.0
                dtick: dTickCalibrate(useInches, df),
                //dtick: useInches ? 4.0 : 10.0,
            },
            autosize: true,
            images: useInches ? imagesInches : images,
        }
    }
}
/*
TC = Total Carbon = total_c (e.g., "0.7678443789482117")
TN = Total Nitrogen = total_n (e.g., "0.0764304623007774")
C:N = Carbon:Nitrogen Ratio = c_n_ratio (e.g., ???)
OC = Organic Carbon = total_oc (e.g., "0.7369314432144165")
X OM = Organic Matter = om (e.g., "1.270469808101654")
IC = Inorganic Carbon = tic (e.g., "0.05")
X BD = Bulk Density = bd (e.g., "1.3782334579404012")
*/

export const healthPlot = (soilProfile: SoilProfile, df: any, preferredLenSmallAbbr: string) => {
  const rawImages = loadImages(soilProfile, df, preferredLenSmallAbbr);

  let cols = df.listColumns();

  // Compute BD if only porosity present
  if (cols.includes('poros') && !cols.includes('bd')) {
    df = df.withColumn('bd', (row: any) => 2.65 * (1 - row.get('poros')));
    cols = df.listColumns();
  }

  // Fallback OC from OM if needed (and if 'oc' not already present)
  if (!cols.includes('total_oc') && cols.includes('om') && !cols.includes('oc')) {
    df = df.withColumn('oc', (row: any) => row.get('om') / 1.72);
    cols = df.listColumns();
  }

  // Depth + units
  const hasDepth = cols.includes('depth (cm)');
  const depthCm = hasDepth ? df.toArray('depth (cm)') : [];
  const useInches = preferredLenSmallAbbr !== 'cm';
  const CM_TO_IN = (1 / 2.5);
  const depthIn = hasDepth ? depthCm.map((v: any) => (Number(v) * CM_TO_IN).toFixed(1)) : [];
  const maxDepthCm = hasDepth ? df.stat.max('depth (cm)') : 60;
  const maxDepthIn = maxDepthCm * CM_TO_IN;
  const depthY = useInches ? depthIn : depthCm;
  const depthMax = useInches ? maxDepthIn : maxDepthCm;

  // Anchor imagery to the left subplot ONLY
  const images = rawImages.map((img: any) => ({
    ...img,
    xref: 'x',
    yref: 'y',
    x: 0,
    sizex: 100,
    y: useInches && typeof img.y === 'number' ? img.y * CM_TO_IN : img.y,
    sizey: useInches && typeof img.sizey === 'number' ? img.sizey * CM_TO_IN : img.sizey,
    layer: 'below',
  }));

  // Data arrays
  const OC  = cols.includes('total_oc') ? df.toArray('total_oc') : (cols.includes('oc') ? df.toArray('oc') : []);
  const TC  = cols.includes('total_c')   ? df.toArray('total_c')   : [];
  const TN  = cols.includes('total_n')   ? df.toArray('total_n')   : [];
  const IC  = cols.includes('tic')       ? df.toArray('tic')       : [];
  const BD  = cols.includes('bd')        ? df.toArray('bd')        : [];
  const CNR = cols.includes('c_n_ratio') ? df.toArray('c_n_ratio') : [];

  // Ranges
  const leftVals: number[] = ([] as any[]).concat(OC, TC, IC, TN)
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v));
  const leftMax = leftVals.length ? Math.max(...leftVals) : 1;
  const leftHi  = Math.max(0.01, leftMax * 1.05);

  const cnVals: number[] = (CNR as any[]).map((v) => Number(v)).filter((v) => Number.isFinite(v));
  const cnMax  = cnVals.length ? Math.max(...cnVals) : 20;
  const cnHi   = Math.max(1, cnMax * 1.1);
  const bdVals = toFiniteNumbers(BD);
  const bdRange = bdVals.length
    ? [Math.min(...bdVals) - 0.1, Math.max(...bdVals) + 0.1]
    : null;

  // Tooltip formatters (rounded + units)
  const fmtPct = (arr: any[]) => arr.map((v) =>
    (v == null || v === '') ? '' : `${dcRound(Number(v))}%`
  );
  const fmtBD  = (arr: any[]) => arr.map((v) =>
    (v == null || v === '') ? '' : `${dcRound(Number(v))} g/cm3`
  );
  const fmtNum = (arr: any[]) => arr.map((v) =>
    (v == null || v === '') ? '' : `${dcRound(Number(v))}`
  );

  // Pre-format customdata for each trace
  const cdOC  = fmtPct(OC);
  const cdTC  = fmtPct(TC);
  const cdIC  = fmtPct(IC);
  const cdTN  = fmtPct(TN);
  const cdBD  = fmtBD(BD);
  const cdCNR = fmtNum(CNR);

  // NOTE: To show the white header with the trace name,
  // DO NOT use "<extra></extra>" — leave <extra> unspecified.
  // hovertemplate content becomes the body; the white header shows the trace name.

  return {
    title: "Carbon",
    traces: [
      {
        x: OC, y: depthY, name: 'Organic Carbon',
        type: 'scatter', connectgaps: true, showlegend: true,
        line: { color: '#800026' }, xaxis: 'x2', yaxis: 'y', orientation: 'h',
        customdata: cdOC,
        hovertemplate: '%{customdata}', // header shows "Organic Carbon"
        hoverlabel: { namelength: -1 }, // show full name
      },
      {
        x: TC, y: depthY, name: 'Total Carbon',
        type: 'scatter', connectgaps: true, showlegend: true,
        line: { color: '#e31a1c' }, xaxis: 'x2', yaxis: 'y', orientation: 'h',
        customdata: cdTC,
        hovertemplate: '%{customdata}',
        hoverlabel: { namelength: -1 },
      },
      {
        x: IC, y: depthY, name: 'Inorganic Carbon',
        type: 'scatter', connectgaps: true, showlegend: true,
        line: { color: '#fd8d3c' }, xaxis: 'x2', yaxis: 'y', orientation: 'h',
        customdata: cdIC,
        hovertemplate: '%{customdata}',
        hoverlabel: { namelength: -1 },
      },
      {
        x: TN, y: depthY, name: 'Total Nitrogen',
        type: 'scatter', connectgaps: true, showlegend: true,
        line: { color: 'gray' }, xaxis: 'x2', yaxis: 'y', orientation: 'h',
        customdata: cdTN,
        hovertemplate: '%{customdata}',
        hoverlabel: { namelength: -1 },
      },
      {
        x: BD, y: depthY, name: 'Bulk Density',
        type: 'scatter', connectgaps: true, showlegend: true, fill: 'tonextx',
        line: { color: '#252525' }, xaxis: 'x3', yaxis: 'y', orientation: 'h',
        customdata: cdBD,
        hovertemplate: '%{customdata}',
        hoverlabel: { namelength: -1 },
      },
      ...(CNR.length ? [{
        x: CNR, y: depthY, name: 'C:N Ratio',
        type: 'scatter', connectgaps: true, showlegend: true,
        line: { color: '#2c7bb6' }, xaxis: 'x4', yaxis: 'y', orientation: 'h',
        customdata: cdCNR,
        hovertemplate: '%{customdata}',
        hoverlabel: { namelength: -1 },
      }] : []),
    ],
    layout: {
      hovermode: "y",
      margin: { t: 0, b: 0, l: 40, r: 0 },
      showLegend: true,
      legend: { orientation: "h", x: 0, y: 0, traceorder: "normal" },

      grid: { subplots: [["xy", "x2y", "x3y"]] },

      xaxis: {
        side: "top", type: "linear", title: { text: "Imagery" },
        range: [0, 100], domain: [0, 0.3], autorange: true,
        showgrid: false, showticklabels: false, fixedrange: true,
      },
      xaxis2: {
        side: "top", type: "linear",
        title: { text: "Carbon & Nitrogen", standoff: 0 },
        domain: [0.35, 0.6], range: [0, leftHi], autorange: false,
      },
      xaxis3: {
        side: "top", type: "linear",
        title: { text: "Bulk Density", standoff: 0 },
        domain: [0.65, 0.9],
        autorange: !bdRange,
        ...(bdRange ? { range: bdRange } : {}),
      },
      ...(CNR.length ? {
        xaxis4: {
          side: "top", type: "linear",
          title: { text: "C:N", standoff: 0 },
          domain: [0.65, 0.9],
          overlaying: 'x3',
          autorange: false,
          range: [0, cnHi],
          tickmode: 'auto',
          showgrid: false,
        },
      } : {}),

      yaxis: {
        side: "left", type: "linear",
        title: { text: useInches ? "depth (in)" : "depth (cm)", standoff: 0 },
        range: [depthMax, 0], domain: [0, 0.9], autorange: false,
        dtick: dTickCalibrate(useInches, df),
        //dtick: useInches ? 4.0 : 10.0,
      },

      autosize: true,
      images,
    },
  };
};



export const conductivityPlot = (df: any, preferredLenSmallAbbr: string) => {
    let availableColumns = df.listColumns();
    console.log("090425 availableColumns", availableColumns);
    df = availableColumns.includes('moisture_cal') ? df.withColumn('moisture_cal', (row: any) => row.get('moisture_cal') / 100) : df;
    df = availableColumns.includes('vres_cal') ? df.withColumn('eca', (row: any) => Math.max(0, 1 / (0.0021594 * row.get('vres_cal')))) : df;
    availableColumns = df.listColumns();
    df = availableColumns.includes('eca') ? df.withColumn('ece', (row: any) => Math.max(0, 5.5 * row.get('eca') - 0.5 )) : df;
    availableColumns = df.listColumns();

    const solubleSaltsSelection = getFirstFiniteSeries(df, [
        'total soluble salts',
        'soluble salts',
        'soluble_salts',
        's__salts',
    ]);
    const eceSelection = getFirstFiniteSeries(df, [
        'ec',
        'ece',
    ]);

    let solubleSalts = solubleSaltsSelection.values;
    let ece = eceSelection.values;
    const solubleSaltsMedian = median(solubleSalts);
    const eceMedian = median(ece);
    const saltsAndEceLookSwapped =
        solubleSaltsMedian !== null &&
        eceMedian !== null &&
        solubleSaltsMedian > 0 &&
        eceMedian > 0 &&
        solubleSaltsMedian < eceMedian &&
        eceMedian / solubleSaltsMedian >= 3;

    if (saltsAndEceLookSwapped) {
        const originalSolubleSalts = solubleSalts;
        solubleSalts = ece;
        ece = originalSolubleSalts;
    }

    // NEW: inch tick labels (display only)
    const { hasDepth, maxDepth, tickvals, ticktext } = makeInchTicksFromCm(df);

    // NEW: convert depth to inches for plotting/hover (dataframe remains in cm)
    const depthCmArr = hasDepth ? df.toArray('depth (cm)') : [];
    const useInches = preferredLenSmallAbbr !== 'cm';
    const cmToIn = (1 / 2.5);
    const depthInArr = hasDepth ? depthCmArr.map((v: any) => (Number(v) * cmToIn).toFixed(1)) : [];
    const maxDepthIn = (hasDepth ? maxDepth : 60) * cmToIn;

    availableColumns = df.listColumns();
    return {
        title: "Conductivity",
        traces: [{
            x: availableColumns.includes('eca') ? df.toArray('eca') : [],
            y: useInches ? depthInArr : depthCmArr,  // inches or cm
            name: 'Apparent Conductivity',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line:{color:'lightblue'},
            xaxis: 'x',
            yaxis: 'y',
            orientation: 'h',
        },
        {
            x: ece,
            y: useInches ? depthInArr : depthCmArr,  // inches or cm
            name: 'Saturated Paste',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line:{color:'lightgreen'},
            xaxis: 'x2',
            yaxis: 'y',
            orientation: 'h',
        },
        {
            x: solubleSalts,
            y: useInches ? depthInArr : depthCmArr,  // inches or cm
            name: 'Soluble Salts',
            type: 'scatter',
            connectgaps: true,
            showlegend: true,
            fill: 'tonextx',
            line:{color:'yellow'},
            xaxis: 'x3',
            yaxis: 'y',
            orientation: 'h',
        }],
        layout: {
            hovermode:"y",
            margin: {
                t: 0,
                b: 0,
                l: 40,
                r: 0
            },
            showLegend: true,
            legend: {
                orientation: "h",
                x: 0, y: 0,
                traceorder: "normal"
            },
            xaxis: {
                side: "top",
                type: "linear",
                title: { text: "ECa", standoff: 0 },
                range: [0, 1500],
                domain: [0, 0.3],
                autorange: true,
            },
            xaxis2: {
                side: "top",
                type: "linear",
                title: { text: "ECe", standoff: 0 },
                range: [0, 1500],
                domain: [0.35, 0.6],
                autorange: true,
            },
            xaxis3: {
                side: "top",
                type: "linear",
                title: { text: "Soluble Salts", standoff: 0 },
                range: [0, 1500],
                domain: [0.65, 0.9],
                autorange: true,
            },
            yaxis: {
                side: "left",
                type: "linear",
                title: { text: useInches ? "depth (in)" : "depth (cm)" },
                range: [useInches ? maxDepthIn : (hasDepth ? maxDepth : 60), 0],
                domain: [0, 0.9],
                autorange: false,
                //dtick: 10.0
                dtick: dTickCalibrate(useInches, df),
                //dtick: useInches ? 4.0 : 10.0,
            },
            autosize: true        }
    }
}

export const saturationAcidityPlot = (
  soilProfile: SoilProfile,
  df: any,
  preferredLenSmallAbbr: string
) => {
  const images = loadImages(soilProfile, df, preferredLenSmallAbbr);
  const cols = df.listColumns();

  // Depth units (in or cm)
  const hasDepth = cols.includes('depth (cm)');
  const depthCm = hasDepth ? df.toArray('depth (cm)') : [];
  const useInches = preferredLenSmallAbbr !== 'cm';
  const CM_TO_IN = (1 / 2.5);
  const depthIn = hasDepth ? depthCm.map((v: any) => (Number(v) * CM_TO_IN).toFixed(1)) : [];
  const maxDepthCm = hasDepth ? df.stat.max('depth (cm)') : 60;
  const maxDepthIn = maxDepthCm * CM_TO_IN;

  // --- Data ---
  // Saturation Percentage: divide by 100 and keep nulls
  const spFrac: Array<number | null> = cols.includes('sp')
    ? df.toArray('sp').map((v: any) =>
        v == null || v === '' ? null : Number(v) / 100
      )
    : cols.includes('ssp')
      ? df.toArray('ssp').map((v: any) =>
          v == null || v === '' ? null : Number(v) / 100
        )
      :[];

  // Range for the SP axis: 0 → max(sp/100) * 1.05
  const spVals: number[] = spFrac.filter(
    (v): v is number => v !== null && Number.isFinite(v)
  );
  const spMax = spVals.length ? Math.max(...spVals) : 1;
  const spHi = Math.max(0.01, spMax * 1.05);

  


  // Exchangeable acidity (no transform)
  //const exchAcid: any[] = cols.includes('exchacid') ? df.toArray('exchacid') : [];
  // pH
  const pH: any[] = cols.includes('ph') ? df.toArray('ph') : [];
  const pHh20: any[] = cols.includes('ph_h20') ? df.toArray('ph_h20') : [];
  const hasPH = hasFiniteValueInRange(pH, 4, 10);
  const hasPHH20 = hasFiniteValueInRange(pHh20, 4, 10);
  // ---- FIX: anchor images to the first subplot's axes (NOT the whole paper) ----
  // Left subplot x-axis range is [0, 100]; domain is [0, 0.3].
  // Tie images to x/y axes so they cannot spill into the middle/right subplots.
  const imagesForLayout = images.map((img: any) => ({
    ...img,
    xref: 'x',      // <-- anchor to first subplot x-axis
    yref: 'y',      // <-- anchor to first subplot y-axis
    x: 0,           // start at left edge of that subplot's x-range
    sizex: 100,     // fill exactly that subplot's x-range (0..100)
    y: useInches && typeof img.y === 'number' ? img.y * CM_TO_IN : img.y,
    sizey:
      useInches && typeof img.sizey === 'number' ? img.sizey * CM_TO_IN : img.sizey,
    layer: 'below',
  }));
  // ---------------------------------------------------------------------------

  const pHTraces = [
    ...(hasPH ? [{
      // Right plot: pH
      x: pH,
      y: useInches ? depthIn : depthCm,
      name: 'pH',
      type: 'scatter',
      connectgaps: true,
      showlegend: true,
      fill: 'none',
      mode: 'lines',
      line: { color: 'gray' },
      xaxis: 'x3',
      yaxis: 'y',
      orientation: 'h',
    }] : []),
    ...(hasPHH20 ? [{
      // Right plot: pH (H2O)
      x: pHh20,
      y: useInches ? depthIn : depthCm,
      name: 'pH (H2O)',
      type: 'scatter',
      connectgaps: true,
      showlegend: true,
      fill: 'none',
      mode: 'lines',
      xaxis: 'x3',
      yaxis: 'y',
      orientation: 'h',
    }] : []),
  ];

  return {
    title: 'pH',
    traces: [
      {
        // Middle plot: Saturation %
        x: spFrac,
        y: useInches ? depthIn : depthCm,
        name: 'Saturation %',
        type: 'scatter',
        connectgaps: true,
        showlegend: true,
        fill: 'tonextx',
        line: { color: 'blue' },
        xaxis: 'x2',
        yaxis: 'y',
        orientation: 'h',
        hovertemplate: '%{x:.0%}<extra></extra>',
      },
      ...pHTraces,
    ],
    layout: {
      hovermode: 'y',
      margin: { t: 0, b: 0, l: 40, r: 0 },
      showLegend: true,
      legend: { orientation: 'h', x: 0, y: 0, traceorder: 'normal' },

      // Keep imagery in the left third only
      grid: { subplots: [['xy', 'x2y', 'x3y']] },

      // Left (imagery) subplot axis
      xaxis: {
        side: 'top',
        type: 'linear',
        title: { text: 'Imagery' },
        range: [0, 100],
        domain: [0, 0.3],
        autorange: true,
        showgrid: false,
        showticklabels: false,
        fixedrange: true,
      },

      // Middle: Saturation %
      xaxis2: {
        side: 'top',
        type: 'linear',
        title: { text: 'Saturation %', standoff: 0  },
        domain: [0.35, 0.6],
        range: [0, spHi],
        tickformat: ',.0%',
        tickangle: -45,
        tickfont: { size: 9 },
      },

      // Right: pH
      xaxis3: {
        side: 'top',
        type: 'linear',
        title: { text: 'pH', standoff: 0 },
        domain: [0.65, 0.9],
        range: [4, 10],
        autorange: false,
        tickmode: 'array',
        tickvals: [4, 5, 6, 7, 8, 9, 10],
      },

      yaxis: {
        side: 'left',
        type: 'linear',
        title: { text: useInches ? 'depth (in)' : 'depth (cm)', standoff: 0 },
        range: [useInches ? maxDepthIn : maxDepthCm, 0],
        domain: [0, 0.9],
        autorange: false,
        dtick: dTickCalibrate(useInches, df),
        //dtick: useInches ? 4.0 : 10.0,
      },

      autosize: true,
      images: imagesForLayout,
      shapes: [
        {
          type: 'rect',
          xref: 'x3',
          yref: 'paper',
          x0: 4,
          x1: 7,
          y0: 0,
          y1: 0.9,
          fillcolor: 'rgba(220, 64, 64, 0.12)',
          line: { width: 0 },
          layer: 'below',
        },
        {
          type: 'rect',
          xref: 'x3',
          yref: 'paper',
          x0: 7,
          x1: 10,
          y0: 0,
          y1: 0.9,
          fillcolor: 'rgba(46, 116, 220, 0.12)',
          line: { width: 0 },
          layer: 'below',
        },
        {
          type: 'line',
          xref: 'x3',
          yref: 'paper',
          x0: 7,
          x1: 7,
          y0: 0,
          y1: 0.9,
          line: { color: 'rgba(80, 80, 80, 0.35)', width: 1 },
          layer: 'below',
        },
      ],
    },
  };
};

